import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { callAPI } from "../helper/apiUtils";
import { BASE_URL } from "../helper/constants";
import { $user } from "../helper/UserFactory";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { DE } from "../Languages/DE";
import { EN } from "../Languages/EN";
import { KJUUP_COLORS } from "../helper/constants";
import { FiSearch } from "react-icons/fi";
import { Oval } from "react-loader-spinner";
import { stripMarkdown, truncateGroupName } from "../utils/conversationUtils";
import teamsLogo from "../Icons/Teams.png";
import "./ChatGroupSelection.css";

// newOnly: a Kjuup + Teams chat always starts a new group (its Teams copy is made for that group),
// so the matching existing groups are not offered.
function ChatGroupSelection({ selectedMemberIds, members, company, onBack, onContinue, onComplete, newOnly = false }) {
  const { isGerm, strings } = useContext(LanguageContext);
  const currentUser = $user.get();
  
  // Check if this is an internal flow (must be boolean, not UUID)
  const isInternal = !!(company && company.id);
  const primaryColor = isInternal ? "var(--kjuup-internal)" : (KJUUP_COLORS?.base_color || "var(--kjuup-accent)");
  // State
  const [groupName, setGroupName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [existingGroups, setExistingGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(50);
  const searchTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isInitialMount = useRef(true);
  const [inputFocused, setInputFocused] = useState(false);

  // Auto-focus input on mount with delay for modal animation
  useEffect(() => {
    const focusTimeout = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 200);
    return () => clearTimeout(focusTimeout);
  }, []);

  // Initial load on mount
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoadingGroups(true);
        
        // If no members selected, pass only the current user's ID
        const usersToPass = (selectedMemberIds && selectedMemberIds.length > 0) 
          ? selectedMemberIds 
          : [currentUser?.id];
        
        const requestBody = {
          users: usersToPass,
          page: 1,
          limit: limit,
          search: "",
        };
        if (company && company.id) {
          requestBody.company = company.id;
        }
        console.log("ChatGroupSelection - Calling getSuggestedGroups with:", requestBody);
        const { data } = await callAPI(
          `/group/getSuggestedGroups`,
          {},
          "POST",
          requestBody
        );
        
        console.log("ChatGroupSelection - Initial API Response:", data, "with company:", company?.id);
        
        if (data && data.status && data.groups && data.groups.data) {
          // Filter to only group chats (not one-to-one)
          const groupChats = data.groups.data.filter((g) => !g.isOneToOne);
          
          if (groupChats.length > 0) {
            const groupsWithSimilarity = groupChats.map(group => ({
              ...group,
              similarity: calculateSimilarity(group)
            }));
            
            groupsWithSimilarity.sort((a, b) => {
              if (b.similarity !== a.similarity) {
                return b.similarity - a.similarity;
              }
              return (a.name || "").localeCompare(b.name || "");
            });
            
            setExistingGroups(groupsWithSimilarity);
            // Check if there are more pages based on totalCount
            const totalPages = Math.ceil((data.groups.totalCount || 0) / limit);
            setHasMore(totalPages > 1);
          } else {
            setExistingGroups([]);
            setHasMore(false);
          }
        } else {
          setExistingGroups([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error in initial load:", error);
        $crud.notify({
          type: "error",
          message: strings.error_loading_groups,
        });
        setExistingGroups([]);
        setHasMore(false);
      } finally {
        setLoadingGroups(false);
      }
    };
    
    // Always call initialLoad (even when no members selected)
    initialLoad();
  }, [selectedMemberIds, company]);

  // Handle search - debounce typing, reload when cleared
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Initial empty load is handled by the mount effect above — skip duplicate call
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // If search is empty, reload initial data
    if (!searchValue.trim()) {
      setDebouncedSearch("");
      setPage(1);
      setExistingGroups([]);
      setHasMore(true);
      // Reload initial data
      const reloadInitial = async () => {
        try {
          setLoadingGroups(true);
          
          // If no members selected, pass only the current user's ID
          const usersToPass = (selectedMemberIds && selectedMemberIds.length > 0) 
            ? selectedMemberIds 
            : [currentUser?.id];
          
          const requestBody = {
            users: usersToPass,
            page: 1,
            limit: limit,
            search: "",
          };
          if (company && company.id) {
            requestBody.company = company.id;
          }
          const { data } = await callAPI(
            `/group/getSuggestedGroups`,
            {},
            "POST",
            requestBody
          );
          
          if (data && data.status && data.groups && data.groups.data) {
            const groupChats = data.groups.data.filter((g) => !g.isOneToOne);
            const groupsWithSimilarity = groupChats.map(group => ({
              ...group,
              similarity: calculateSimilarity(group)
            }));
            
            groupsWithSimilarity.sort((a, b) => {
              if (b.similarity !== a.similarity) {
                return b.similarity - a.similarity;
              }
              return (a.name || "").localeCompare(b.name || "");
            });
            
            setExistingGroups(groupsWithSimilarity);
            const totalPages = Math.ceil((data.groups.totalCount || 0) / limit);
            setHasMore(totalPages > 1);
          }
        } catch (error) {
          console.error("Error reloading:", error);
        } finally {
          setLoadingGroups(false);
        }
      };
      // Always reload (even when no members selected)
      reloadInitial();
      return;
    }

    // Wait for typing to settle before searching (3+ chars)
    if (searchValue.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(searchValue.trim());
        setPage(1);
        setExistingGroups([]);
        setHasMore(true);
      }, 400);
    } else {
      // If less than 3 characters, clear results
      setDebouncedSearch("");
      setExistingGroups([]);
      setHasMore(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue, selectedMemberIds, company]);

  // Load groups when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== undefined && debouncedSearch !== "") {
      loadChatGroups(1, debouncedSearch);
    }
  }, [debouncedSearch]);

  // Load more when page changes (for pagination)
  useEffect(() => {
    if (page > 1) {
      loadChatGroups(page, debouncedSearch || "", true);
    }
  }, [page]);

  // Calculate similarity score between selected members and group members
  const calculateSimilarity = (group) => {
    if (!group.users || group.users.length === 0) return 0;
    
    const groupMemberIds = group.users.map(u => u.id).filter(id => id !== currentUser?.id);
    const selectedIds = selectedMemberIds.filter(id => id !== currentUser?.id);
    
    // When no members are selected, return 1 for all groups (show all with equal priority)
    if (selectedIds.length === 0) return 1;
    
    // Count how many selected members are in this group
    const commonMembers = selectedIds.filter(id => groupMemberIds.includes(id)).length;
    
    // Calculate similarity: common members / total selected members
    return commonMembers / selectedIds.length;
  };

  // Load existing chat groups with pagination and search
  const loadChatGroups = async (pageNum = 1, searchQuery = "", append = false) => {
    if (append && (loadingMore || loadingGroups)) return;
    if (!append && loadingGroups && existingGroups.length > 0 && pageNum === 1) return;
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingGroups(true);
      }

      // If no members selected, pass only the current user's ID
      const usersToPass = (selectedMemberIds && selectedMemberIds.length > 0) 
        ? selectedMemberIds 
        : [currentUser?.id];

      const requestBody = {
        users: usersToPass,
        page: pageNum,
        limit: limit,
        search: searchQuery || "",
      };
      if (company && company.id) {
        requestBody.company = company.id;
      }
      const { data } = await callAPI(
        `/group/getSuggestedGroups`,
        {},
        "POST",
        requestBody
      );
      
      if (data && data.status && data.groups && data.groups.data) {
        // Filter to only group chats (not one-to-one)
        const groupChats = data.groups.data.filter((g) => !g.isOneToOne);
        
        if (groupChats.length === 0 && !append) {
          setExistingGroups([]);
          setHasMore(false);
          return;
        }
        
        // Calculate similarity for each group
        const groupsWithSimilarity = groupChats.map(group => ({
          ...group,
          similarity: calculateSimilarity(group)
        }));
        
        // Sort by similarity (highest first), then by name
        groupsWithSimilarity.sort((a, b) => {
          if (b.similarity !== a.similarity) {
            return b.similarity - a.similarity;
          }
          return (a.name || "").localeCompare(b.name || "");
        });
        
        if (append) {
          setExistingGroups((prev) => [...prev, ...groupsWithSimilarity]);
        } else {
          setExistingGroups(groupsWithSimilarity);
        }
        
        // Check if there are more pages based on totalCount
        const totalPages = Math.ceil((data.groups.totalCount || 0) / limit);
        setHasMore(pageNum < totalPages);
      } else {
        if (!append) {
          setExistingGroups([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading chat groups:", error);
      if (!append) {
        $crud.notify({
          type: "error",
          message: strings.error_loading_groups,
        });
        setExistingGroups([]);
      }
      setHasMore(false);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoadingGroups(false);
      }
    }
  };

  // Handle scroll for infinite pagination
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasMore && !loadingMore && !loadingGroups && debouncedSearch !== undefined) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, loadingMore, loadingGroups, debouncedSearch]);

  // Handle continue
  const handleContinue = () => {
    if (selectedGroup) {
      // Use existing group - open directly (skip room selection)
      onContinue(selectedGroup, true); // Pass true to indicate it's an existing group
    } else if (groupName.trim()) {
      // Create new group with provided name - continue to room selection
      onContinue({ name: groupName.trim(), isNew: true, company: company }, false);
    } else {
      $crud.notify({
        type: "error",
        message: strings.please_select_or_enter_group_name,
      });
    }
  };

  // Get member names for display
  const getMemberNames = (group) => {
    if (!group.users || group.users.length === 0) return "";
    const memberNames = group.users
      .filter(u => u.id !== currentUser?.id)
      .slice(0, 3)
      .map(u => u.name || u.fullName || `${u.firstName} ${u.lastName}`.trim())
      .filter(Boolean);
    
    if (group.users.length > 4) {
      return memberNames.join(", ");
    }
    return memberNames.join(", ");
  };

  return (
    <div
      className="create-message-dialog"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="create-message-header">
        <button
          onClick={onBack}
          className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
        >
          <span className="material-symbols-outlined">keyboard_arrow_left</span>
        </button>
        <h5 className="create-message-header-title-sm">
          {strings.subject}
        </h5>
        <button
          onClick={handleContinue}
          disabled={loading || (!selectedGroup && !groupName.trim())}
          className={`kj-btn kj-btn--ghost kj-btn--sm${isInternal ? ' kj-btn--internal' : ''}`}
        >
          {strings.continue_btn}
          {!loading && <span className="material-symbols-outlined">keyboard_arrow_right</span>}
        </button>
      </div>

      {/* Search/Input Bar */}
      <div className="create-message-search-section">
        <div className="create-message-search-wrapper">
          <input
            ref={inputRef}
            type="text"
            autoFocus
            maxLength={30}
            value={selectedGroup ? stripMarkdown(selectedGroup.name) : (searchValue || groupName)}
            onChange={(e) => {
              const value = e.target.value;
              if (selectedGroup) {
                setSelectedGroup(null);
              }
              setSearchValue(value);
              if (!value) {
                setGroupName("");
              } else if (!selectedGroup) {
                setGroupName(value);
              }
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder={strings.add_subject}
            className={`create-message-search-input-no-icon chat-group-selection__input ${isInternal ? 'internal' : ''} ${inputFocused ? 'focused' : ''}`}
          />
        </div>
        <span className="text-muted chat-group-selection__counter">
          {30 - (selectedGroup ? stripMarkdown(selectedGroup.name) : (searchValue || groupName)).length} {strings.characters_remaining}
        </span>
      </div>

      {/* Existing Groups List */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="create-message-list"
      >
        {newOnly ? (
          <div className="create-message-list-empty chat-group-selection__teams-note">
            <img src={teamsLogo} alt="" className="chat-group-selection__teams-logo" />
            <p>
              {strings.combined_new_group_note ||
                (isGerm
                  ? "Kjuup- und Teams-Kontakte: Es wird eine neue Gruppe erstellt, die auch in Microsoft Teams erscheint."
                  : "Kjuup and Teams contacts: a new group is created that also appears in Microsoft Teams.")}
            </p>
          </div>
        ) : loadingGroups && existingGroups.length === 0 ? (
          <div className="create-message-list-loading">
            <Oval
              height={40}
              width={40}
              color={primaryColor}
              wrapperStyle={{}}
              visible={true}
              ariaLabel="loading"
              strokeWidth={5}
            />
          </div>
        ) : existingGroups.length === 0 ? (
          <div className="create-message-list-empty">
            <p>{strings.no_groups_found}</p>
          </div>
        ) : (
          <>
            <p className="create-message-list-section-title">
              {strings.matching_existing_chats}
            </p>
            <div className="create-message-card-grid">
              {existingGroups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => {
                    setSelectedGroup(group);
                    setGroupName("");
                  }}
                  className={`create-message-card-item chat-group-selection__card ${selectedGroup?.id === group.id ? 'selected' : ''} ${isInternal ? 'internal' : ''}`}
                >
                  <div className="create-message-card-content">
                    <p className="create-message-card-title">
                      {group.name === 'All' ? strings.all_members : (truncateGroupName(stripMarkdown(group.name)) || strings.unnamed_group)}
                    </p>
                    <p className="create-message-card-subtitle">
                      {getMemberNames(group)}
                    </p>
                    <p className="create-message-card-meta">
                      {strings.room_label} {group.buildingName || "-"}
                    </p>
                  </div>
                  <div className="create-message-card-checkbox">
                    <div className={`custom_checkbox chat-group-selection__checkbox ${isInternal ? 'internal' : ''}`}>
                      <label className="role-label chat-group-selection__check-label">
                        <input
                          checked={selectedGroup?.id === group.id}
                          disabled
                          type="checkbox"
                          value={group.id}
                        />
                        <span className={`checkmark-label chat-group-selection__checkmark ${isInternal ? 'internal' : ''}`} />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {loadingMore && (
              <div className="create-message-loading-more">
                <Oval
                  height={30}
                  width={30}
                  color={primaryColor}
                  wrapperStyle={{}}
                  visible={true}
                  ariaLabel="loading"
                  strokeWidth={4}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ChatGroupSelection;

